const MAX_IMAGE_EDGE = 30000;
const MAX_IMAGE_PIXELS = 250000000;
const STANDARD_IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif']);
const RAW_IMAGE_EXTENSIONS = new Set([
  'arw', 'cr2', 'cr3', 'crw', 'dng', 'nef', 'nrw', 'orf', 'raf', 'rw2', 'pef', 'sr2', 'srf',
]);
const IMAGE_EXTENSIONS = new Set([...STANDARD_IMAGE_EXTENSIONS, ...RAW_IMAGE_EXTENSIONS]);
const IMAGE_ACCEPT = Array.from(IMAGE_EXTENSIONS).map(extension => `.${extension}`).join(',');

class ImageUploadValidationError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'ImageUploadValidationError';
    this.code = code;
  }
}

function getImageExtension(fileName) {
  const value = String(fileName || '').trim().toLowerCase();
  const dot = value.lastIndexOf('.');
  return dot >= 0 ? value.slice(dot + 1) : '';
}

function isSupportedImageFile(file) {
  return IMAGE_EXTENSIONS.has(getImageExtension(file?.name));
}

function isRawImageFile(file) {
  return RAW_IMAGE_EXTENSIONS.has(getImageExtension(file?.name));
}

function validateImageFileBasics(file) {
  if (!isSupportedImageFile(file)) {
    throw new ImageUploadValidationError(
      'UNSUPPORTED_IMAGE_FORMAT',
      '仅支持 JPG、PNG、WebP、HEIC、HEIF 和常见相机 RAW 图片。'
    );
  }
  if (!Number.isSafeInteger(file?.size) || file.size <= 0) {
    throw new ImageUploadValidationError(
      'INVALID_IMAGE_FILE_SIZE',
      '图片文件不能为空。'
    );
  }
}

function loadImageDimensionsWithElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ImageUploadValidationError('INVALID_IMAGE_CONTENT', '无法读取图片尺寸，请确认文件未损坏。'));
    };
    image.src = url;
  });
}

async function readImageDimensions(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      const dimensions = { width: bitmap.width, height: bitmap.height };
      bitmap.close?.();
      return dimensions;
    } catch (error) {
      // Safari and older Chromium builds support some formats only through an
      // image element, so retain a non-mutating fallback.
    }
  }
  return loadImageDimensionsWithElement(file);
}

function validateImageDimensions(width, height) {
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width <= 0 || height <= 0) {
    throw new ImageUploadValidationError('INVALID_IMAGE_DIMENSIONS', '无法读取图片尺寸。');
  }
  if (width > MAX_IMAGE_EDGE || height > MAX_IMAGE_EDGE) {
    throw new ImageUploadValidationError('IMAGE_DIMENSIONS_TOO_LARGE', '图片单边不能超过 30000 像素。');
  }
  if (width * height > MAX_IMAGE_PIXELS) {
    throw new ImageUploadValidationError('IMAGE_PIXELS_TOO_LARGE', '图片总像素不能超过 2.5 亿。');
  }
  return { width, height };
}

async function validateImageFile(file) {
  validateImageFileBasics(file);
  if (isRawImageFile(file)) {
    throw new ImageUploadValidationError('RAW_REQUIRES_DECODER', 'RAW 图片需要先经过专用解码。');
  }
  const { width, height } = await readImageDimensions(file);
  return validateImageDimensions(width, height);
}

export {
  IMAGE_ACCEPT,
  IMAGE_EXTENSIONS,
  RAW_IMAGE_EXTENSIONS,
  STANDARD_IMAGE_EXTENSIONS,
  MAX_IMAGE_EDGE,
  MAX_IMAGE_PIXELS,
  ImageUploadValidationError,
  getImageExtension,
  isRawImageFile,
  isSupportedImageFile,
  readImageDimensions,
  validateImageDimensions,
  validateImageFile,
  validateImageFileBasics,
};
